/* eslint-disable */
import { useEffect, useState } from "react";
import { getAdvancesReport, downloadAdvancesReport } from "../lib/api";
import { AdvancesFilters } from "../components/advances/AdvancesFilters";
import { AdvancesTable } from "../components/advances/AdvancesTable";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const eur = (n) =>
  (Number(n) || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR", minimumFractionDigits: 2, useGrouping: "always" });

const Stat = ({ label, value, testid }) => (
  <div className="rounded-lg border border-slate-200 bg-white px-5 py-4">
    <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className="font-cabinet text-2xl font-bold text-slate-900 mt-1" data-testid={testid}>{value}</div>
  </div>
);

export default function AdvancesByYear() {
  const [tripYear, setTripYear] = useState(new Date().getFullYear() + 1);
  const [issueYear, setIssueYear] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null);
  const params = { trip_year: tripYear, ...(issueYear ? { issue_year: issueYear } : {}) };

  useEffect(() => {
    setLoading(true);
    getAdvancesReport(params)
      .then(setReport)
      .catch((e) => toast.error("Error: " + (e?.response?.data?.detail || e.message)))
      .finally(() => setLoading(false));
  }, [tripYear, issueYear]);

  const handleExport = async (fmt) => {
    setExporting(fmt);
    try {
      await downloadAdvancesReport(fmt, params);
    } catch (e) {
      toast.error("No se pudo exportar: " + e.message);
    } finally {
      setExporting(null);
    }
  };

  const invoicesCount = new Set((report?.rows || []).map((r) => r.invoice_id)).size;
  const skipped = report?.invoices_without_detail || [];

  return (
    <div data-testid="advances-by-year-page" className="space-y-6 max-w-5xl">
      <header>
        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 mb-1">Contabilidad · Periodificación</div>
        <h1 className="font-cabinet text-3xl font-bold tracking-tight text-slate-900">Adelantos por año de viaje</h1>
        <p className="text-sm text-slate-500 mt-2">
          Lo facturado como adelanto, cliente a cliente, de los viajes que empiezan en el año elegido.
          Solo facturas de adelanto emitidas.
        </p>
      </header>

      <AdvancesFilters
        report={report}
        tripYear={tripYear}
        issueYear={issueYear}
        onTripYear={setTripYear}
        onIssueYear={setIssueYear}
        onExport={handleExport}
        exporting={exporting}
      />

      {loading ? (
        <div className="py-16 flex justify-center text-slate-400"><Loader2 className="animate-spin" /></div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Stat label="Total adelantado" value={eur(report?.total)} testid="advances-total" />
            <Stat label="Viajes" value={report?.count ?? 0} testid="advances-count" />
            <Stat label="Facturas de adelanto" value={invoicesCount} testid="advances-invoices-count" />
          </div>

          {report?.count ? (
            <AdvancesTable rows={report.rows} total={report.total} />
          ) : (
            <div className="rounded-lg border border-dashed border-slate-200 py-12 text-center text-sm text-slate-500" data-testid="advances-empty">
              No hay adelantos facturados de viajes que empiecen en {tripYear}
              {issueYear ? ` emitidos en ${issueYear}` : ""}.
            </div>
          )}

          {skipped.length > 0 && (
            <p className="text-xs text-slate-500" data-testid="advances-skipped-invoices">
              {skipped.length} {skipped.length === 1 ? "factura antigua no tiene" : "facturas antiguas no tienen"} detalle
              por viaje y no se pueden desglosar: {skipped.join(", ")}.
            </p>
          )}
        </>
      )}
    </div>
  );
}
